import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Server, Network, Cpu, Globe, ShieldCheck, Headset, CheckCircle2, Trophy, Users, Globe2 } from 'lucide-react';
import { HeroBackground } from '../components/HeroBackground';
import { ServiceCard } from '../components/ServiceCard';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { SectionHeading } from '../components/SectionHeading';
import { TestimonialSlider } from '../components/TestimonialSlider';
import { CTABanner } from '../components/CTABanner';
export function HomePage() {
  const services = [{
    icon: <Server className="w-7 h-7" data-id="element-320" />,
    title: 'ICT Infrastructure',
    description: 'End-to-end lifecycle management — from procurement and warehousing to deployment and maintenance by vendor-certified experts.'
  }, {
    icon: <Network className="w-7 h-7" data-id="element-321" />,
    title: 'Systems Integration',
    description: 'Experienced IT consultants deploying a full spectrum of technology — build, configure, and prepare equipment for seamless onsite installation.'
  }, {
    icon: <ShieldCheck className="w-7 h-7" data-id="element-322" />,
    title: 'Managed IT Services',
    description: 'Proactive monitoring, maintenance, and support to keep your business running 24/7.'
  }, {
    icon: <Cpu className="w-7 h-7" data-id="element-323" />,
    title: 'Business Process Mgmt',
    description: 'Comprehensive 5-step BPM cycle — from business objectives through modeling, automation, monitoring, and optimization.'
  }, {
    icon: <Globe className="w-7 h-7" data-id="element-324" />,
    title: 'Digital Inclusion',
    description: 'Promoting digital inclusion through analysis of economic and social impacts of ICT trends to accelerate growth and social inclusion.'
  }, {
    icon: <Headset className="w-7 h-7" data-id="element-325" />,
    title: 'ICT Products Delivery',
    description: 'End-to-end delivery and marketing outsourcing of premium enterprise hardware and software solutions.'
  }];
  const features = [{
    icon: <Trophy data-id="element-326" />,
    title: 'Proven Track Record',
    desc: 'Over a decade of delivering complex projects on time and within budget.'
  }, {
    icon: <Network data-id="element-327" />,
    title: 'End-to-End Solutions',
    desc: 'From consultation to deployment and ongoing support, we handle it all.'
  }, {
    icon: <Globe2 data-id="element-328" />,
    title: 'Local Expertise, Global Standards',
    desc: 'Deep understanding of the African market combined with international best practices.'
  }, {
    icon: <Users data-id="element-329" />,
    title: 'Dedicated Support',
    desc: 'A committed team of certified professionals available round the clock.'
  }];
  const clients = [{
    name: 'Bank of Industry',
    logo: 'https://upload.wikimedia.org/wikipedia/en/5/54/Bank_of_Industry_logo.png'
  }, {
    name: 'ECOWAS',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/0/0e/Flag_of_ECOWAS.svg/200px-Flag_of_ECOWAS.svg.png'
  }, {
    name: 'Bank of Agriculture',
    logo: null
  }, {
    name: 'SPDC',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/e/e8/Shell_logo.svg/150px-Shell_logo.svg.png'
  }, {
    name: 'DFID',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/DFID_logo.svg/200px-DFID_logo.svg.png'
  }, {
    name: 'Niger State Government',
    logo: null
  }, {
    name: 'Embassy of Belarus',
    logo: null
  }, {
    name: 'Subotech Nig. Ltd',
    logo: null
  }];
  return <main className="w-full" data-id="element-330">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-navy-900" data-id="element-331">
        <HeroBackground data-id="element-332" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full" data-id="element-333">
          <div className="max-w-4xl" data-id="element-334">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-335">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6" data-id="element-336">
                <span className="w-2 h-2 rounded-full bg-tech-green animate-pulse" data-id="element-337" />
                <span className="text-sm font-medium text-slate-300" data-id="element-338">
                  Trusted Technology Partner in Africa
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight mb-6" data-id="element-339">
                Powering Digital <br data-id="element-340" />
                <span className="text-gradient-tech" data-id="element-341">Transformation</span>{' '}
                <br data-id="element-342" />
                Across Africa
              </h1>

              <p className="text-xl text-slate-300 mb-10 max-w-2xl font-light leading-relaxed" data-id="element-343">
                We design, deploy, and manage enterprise-grade ICT
                infrastructure that drives growth, efficiency, and innovation
                for governments and large organizations.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-344">
                <Link to="/contact" className="px-8 py-4 text-center text-base font-semibold text-white bg-tech-green rounded-full hover:bg-emerald-400 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)]" data-id="element-345">
                  Get Started
                </Link>
                <Link to="/services" className="px-8 py-4 text-center text-base font-semibold text-white border border-white/20 rounded-full hover:bg-white/10 backdrop-blur-sm transition-all" data-id="element-346">
                  Our Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 1
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50" data-id="element-347">
          <span className="text-xs uppercase tracking-widest" data-id="element-348">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" data-id="element-349" />
        </motion.div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-navy-900 relative" data-id="element-350">
        <div className="max-w-7xl mx-auto px-4" data-id="element-351">
          <SectionHeading title="Comprehensive ICT Solutions" subtitle="We provide end-to-end technology services tailored to meet the complex demands of modern enterprises." light data-id="element-352" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-id="element-353">
            {services.map((service, idx) => <ServiceCard key={idx} icon={service.icon} title={service.title} description={service.description} delay={idx * 0.1} data-id="element-354" />)}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white border-y border-slate-100" data-id="element-355">
        <div className="max-w-7xl mx-auto px-4" data-id="element-356">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-slate-100" data-id="element-357">
            <AnimatedCounter end={200} suffix="+" label="Projects Delivered" data-id="element-358" />
            <AnimatedCounter end={50} suffix="+" label="Enterprise Clients" data-id="element-359" />
            <AnimatedCounter end={15} suffix="+" label="Years Experience" data-id="element-360" />
            <AnimatedCounter end={6} label="Countries Served" data-id="element-361" />
          </div>
        </div>
      </section>

      {/* Featured Clients */}
      <section className="py-20 bg-slate-50" data-id="element-362">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-363">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-10" data-id="element-364">
            Trusted by Industry Leaders
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 md:gap-8" data-id="element-365">
            {clients.map((client, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.08
          }} className="bg-white rounded-2xl p-6 flex items-center justify-center h-24 border border-slate-100 shadow-sm hover:shadow-md hover:border-tech-blue/20 transition-all group" data-id="element-366">
                {client.logo ? <img src={client.logo} alt={client.name} className="max-h-12 max-w-[140px] object-contain grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300" data-id="element-367" /> : <span className="text-lg font-bold text-navy-900/50 group-hover:text-navy-900 transition-colors duration-300 text-center leading-tight" data-id="element-368">
                    {client.name}
                  </span>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-369">
        {/* Decorative background */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-tech-blue/5 blur-[100px] rounded-full" data-id="element-370" />

        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-371">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-372">
            <div data-id="element-373">
              <SectionHeading title="Why Partner With Invigo?" subtitle="We don't just supply technology; we engineer solutions that align with your strategic business objectives." light data-id="element-374" />
              <p className="text-slate-300 mb-8 leading-relaxed" data-id="element-375">
                Invigo Technology is a fully rebranded and enhanced company,
                formally known as Inspired Delta Technology Solutions. The
                company's technology independence, global talent and extensive
                partner network help clients harness the power of innovation to
                thrive on change and guide their digital transformation
                journeys.
              </p>
              <Link to="/about" className="inline-flex items-center text-tech-green font-semibold hover:text-emerald-400 transition-colors" data-id="element-376">
                Learn more about our journey &rarr;
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-377">
              {features.map((feature, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="glass-card p-6 rounded-2xl" data-id="element-378">
                  <div className="w-12 h-12 rounded-lg bg-tech-blue/20 text-tech-blue flex items-center justify-center mb-4" data-id="element-379">
                    {feature.icon}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2" data-id="element-380">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-slate-400" data-id="element-381">{feature.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-slate-50" data-id="element-382">
        <div className="max-w-7xl mx-auto px-4" data-id="element-383">
          <SectionHeading title="Client Success Stories" centered data-id="element-384" />
          <div className="mt-16" data-id="element-385">
            <TestimonialSlider data-id="element-386" />
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner data-id="element-387" />
    </main>;
}